if (!customElements.get('newsletter-popup')) {
  customElements.define(
    'newsletter-popup',
    class NewsletterPopup extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.dialog = this.querySelector('.newsletter-popup__dialog');
        this.form = this.querySelector('.newsletter-popup__form');
        this.formView = this.querySelector('[data-newsletter-popup-form-view]');
        this.successView = this.querySelector('[data-newsletter-popup-success-view]');
        this.errorEl = this.querySelector('[data-newsletter-popup-error]');
        this.submitButton = this.form ? this.form.querySelector('.newsletter-popup__submit') : null;
        this.submitLabel = this.querySelector('[data-newsletter-popup-submit-label]');
        this.spinner = this.querySelector('.newsletter-popup__spinner');

        this.storageKey = this.dataset.storageKey || 'vamos-newsletter-popup';
        this.pendingKey = this.storageKey + '-pending';
        this.delayMs = Math.max(0, (parseInt(this.dataset.delay, 10) || 10) * 1000);
        this.showAgainDays = Math.max(1, parseInt(this.dataset.showAgainDays, 10) || 7);
        this.discountCode = (this.dataset.discountCode || '').trim();
        this.designMode = this.dataset.designMode === 'true';
        this.pageHidden = this.dataset.pageHidden === 'true';
        this.openTimer = null;
        this.previouslyFocused = null;
        this.hasSubscribed = false;

        this.onKeyDown = this.onKeyDown.bind(this);
        this.onSubmit = this.onSubmit.bind(this);

        this.querySelectorAll('[data-newsletter-popup-close]').forEach((el) => {
          el.addEventListener('click', () => this.close(true));
        });

        if (this.form) {
          this.form.addEventListener('submit', this.onSubmit);
        }

        if (this.designMode) {
          this.open();
          return;
        }

        // Always handle subscribe return (even on pages where auto-open is hidden).
        if (this.handlePostSubscribeReturn()) {
          return;
        }

        if (this.pageHidden) return;
        if (!this.shouldShow()) return;

        this.openTimer = window.setTimeout(() => {
          this.open();
        }, this.delayMs);
      }

      disconnectedCallback() {
        if (this.openTimer) {
          window.clearTimeout(this.openTimer);
          this.openTimer = null;
        }
        document.removeEventListener('keydown', this.onKeyDown);
        document.body.classList.remove('newsletter-popup-open');
      }

      getCookie(name) {
        try {
          const match = document.cookie.match(
            new RegExp('(?:^|; )' + name.replace(/[$()*+.?[\\\]^{|}]/g, '\\$&') + '=([^;]*)')
          );
          return match ? decodeURIComponent(match[1]) : '';
        } catch (e) {
          return '';
        }
      }

      setCookie(name, value, maxAgeSeconds) {
        try {
          document.cookie =
            name +
            '=' +
            encodeURIComponent(value) +
            '; path=/; max-age=' +
            maxAgeSeconds +
            '; SameSite=Lax';
        } catch (e) {}
      }

      clearPending() {
        try {
          window.sessionStorage.removeItem(this.pendingKey);
        } catch (e) {}
        this.setCookie(this.pendingKey, '', 0);
      }

      setPending() {
        try {
          window.sessionStorage.setItem(this.pendingKey, '1');
        } catch (e) {}
        // Cookie backup — survives some redirects better than sessionStorage alone.
        this.setCookie(this.pendingKey, '1', 600);
      }

      wasPending() {
        try {
          if (window.sessionStorage.getItem(this.pendingKey) === '1') return true;
        } catch (e) {}
        return this.getCookie(this.pendingKey) === '1';
      }

      cleanSubscribeParams() {
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete('customer_posted');
          url.searchParams.delete('newsletter_subscribed');
          url.searchParams.delete('contact_posted');
          if (url.hash === '#contact_form' || url.hash === '#newsletter-popup') {
            url.hash = '';
          }
          window.history.replaceState({}, '', url.pathname + url.search + url.hash);
        } catch (e) {}
      }

      isSubscribeReturn() {
        try {
          const params = new URLSearchParams(window.location.search);
          if (
            params.get('customer_posted') === 'true' ||
            params.get('newsletter_subscribed') === '1' ||
            params.get('contact_posted') === 'true'
          ) {
            return true;
          }
          if (window.location.hash === '#contact_form' && this.wasPending()) {
            return true;
          }
        } catch (e) {}
        return false;
      }

      handlePostSubscribeReturn() {
        if (!this.isSubscribeReturn()) {
          // Stale pending from abandoned captcha / submit.
          if (this.wasPending()) this.clearPending();
          return false;
        }

        this.clearPending();
        this.markSubscribed();
        this.cleanSubscribeParams();
        this.showSuccess();
        this.open();
        this.applyDiscountCode().catch(function () {});
        return true;
      }

      readStatus() {
        const cookieStatus = this.getCookie(this.storageKey);
        if (cookieStatus === 'subscribed' || cookieStatus === 'dismissed') {
          return { status: cookieStatus, ts: Date.now() };
        }

        try {
          const raw = window.localStorage.getItem(this.storageKey);
          if (!raw) return null;
          const data = JSON.parse(raw);
          if (!data || typeof data !== 'object') return null;
          return data;
        } catch (e) {
          return null;
        }
      }

      shouldShow() {
        const data = this.readStatus();
        if (!data) return true;

        if (data.status === 'subscribed') return false;

        if (data.status === 'dismissed' && data.ts) {
          const expiresAt = Number(data.ts) + this.showAgainDays * 24 * 60 * 60 * 1000;
          if (Date.now() < expiresAt) return false;
        }

        return true;
      }

      markSubscribed() {
        this.hasSubscribed = true;
        this.persist('subscribed');
      }

      persist(status) {
        const payload = JSON.stringify({
          status: status,
          ts: Date.now(),
        });

        try {
          window.localStorage.setItem(this.storageKey, payload);
        } catch (e) {}

        // 1 year for subscribed; show-again window for dismissed.
        const maxAge =
          status === 'subscribed'
            ? 365 * 24 * 60 * 60
            : this.showAgainDays * 24 * 60 * 60;
        this.setCookie(this.storageKey, status, maxAge);
      }

      open() {
        if (this.classList.contains('is-open')) return;

        this.previouslyFocused = document.activeElement;
        this.hidden = false;
        requestAnimationFrame(() => {
          this.classList.add('is-open');
        });
        document.body.classList.add('newsletter-popup-open');
        document.addEventListener('keydown', this.onKeyDown);

        const focusTarget =
          this.successView && !this.successView.hidden
            ? this.querySelector('[data-newsletter-popup-close].newsletter-popup__submit') ||
              this.dialog
            : this.form && this.form.querySelector('input[type="email"]')
              ? this.form.querySelector('input[type="email"]')
              : this.dialog;

        if (focusTarget) {
          window.setTimeout(() => focusTarget.focus(), 50);
        }
      }

      close(persistDismiss) {
        if (this.openTimer) {
          window.clearTimeout(this.openTimer);
          this.openTimer = null;
        }

        this.classList.remove('is-open');
        document.body.classList.remove('newsletter-popup-open');
        document.removeEventListener('keydown', this.onKeyDown);

        const onSuccess =
          this.hasSubscribed || (this.successView && !this.successView.hidden);

        if (onSuccess) {
          // Closing success screen — never show again.
          this.markSubscribed();
        } else if (persistDismiss && !this.designMode) {
          this.persist('dismissed');
        }

        window.setTimeout(() => {
          this.hidden = true;
          if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
            try {
              this.previouslyFocused.focus();
            } catch (e) {}
          }
        }, 280);
      }

      onKeyDown(event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close(true);
        }
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;
        this.submitButton.disabled = isLoading;
        if (this.spinner) this.spinner.hidden = !isLoading;
        if (this.submitLabel) this.submitLabel.hidden = isLoading;
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.hidden = !message;
        this.errorEl.textContent = message || '';
      }

      showSuccess() {
        if (this.formView) this.formView.hidden = true;
        if (this.successView) this.successView.hidden = false;
        this.markSubscribed();
      }

      cartRoot() {
        return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root
          : '/';
      }

      async applyDiscountCode() {
        if (!this.discountCode) return { applied: false };

        let existing = [];
        const holder = document.querySelector('[data-cart-applied-codes]');
        if (holder) {
          existing = (holder.getAttribute('data-cart-applied-codes') || '')
            .split('|')
            .map((s) => s.trim())
            .filter(Boolean);
        }

        const combined = existing.slice();
        if (
          !combined.some((code) => code.toLowerCase() === this.discountCode.toLowerCase())
        ) {
          combined.push(this.discountCode);
        }

        const response = await fetch(this.cartRoot() + 'cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            discount: combined.join(','),
            sections: 'cart-drawer,cart-icon-bubble',
            sections_url: window.location.pathname,
          }),
        });

        if (!response.ok) {
          throw new Error('discount_failed');
        }

        const cart = await response.json();
        let applied = false;

        if (cart && Array.isArray(cart.discount_codes)) {
          applied = cart.discount_codes.some(
            (entry) =>
              entry &&
              entry.code &&
              entry.code.toLowerCase() === this.discountCode.toLowerCase() &&
              entry.applicable
          );
        }

        if (cart && cart.sections) {
          this.refreshCartSections(cart.sections);
        }

        return { applied: applied, cart: cart };
      }

      refreshCartSections(sections) {
        if (!sections) return;

        Object.keys(sections).forEach((sectionId) => {
          const html = sections[sectionId];
          if (!html) return;

          const selector =
            sectionId === 'cart-drawer'
              ? 'cart-drawer'
              : sectionId === 'cart-icon-bubble'
                ? '#shopify-section-cart-icon-bubble, .shopify-section-header-icons, #cart-icon-bubble'
                : `#shopify-section-${sectionId}`;

          const targets = document.querySelectorAll(selector);
          if (!targets.length) return;

          const doc = new DOMParser().parseFromString(html, 'text/html');

          targets.forEach((target) => {
            if (sectionId === 'cart-drawer') {
              const next = doc.querySelector('cart-drawer');
              if (next && target.tagName === 'CART-DRAWER') {
                target.innerHTML = next.innerHTML;
              }
              return;
            }

            if (sectionId === 'cart-icon-bubble') {
              const bubble = doc.querySelector('#cart-icon-bubble') || doc.body.firstElementChild;
              const live = document.getElementById('cart-icon-bubble');
              if (live && bubble) {
                live.innerHTML = bubble.innerHTML;
              }
              return;
            }

            const nextSection =
              doc.querySelector(`#shopify-section-${sectionId}`) || doc.body.firstElementChild;
            if (nextSection) target.innerHTML = nextSection.innerHTML;
          });
        });

        document.dispatchEvent(new CustomEvent('cart:updated'));
      }

      onSubmit() {
        this.showError('');
        this.setPending();
        this.setLoading(true);
      }
    }
  );
}
